import { cva, type VariantProps } from 'class-variance-authority';

import { Label } from '../../primitives/label';
import { LoadingAddon } from '../../primitives/loading-addon';
import { Switch } from '../../primitives/switch';

import { cn } from '../../utils/cn';

import type { JSX } from 'react';

const SWITCH_ID = crypto?.randomUUID() ?? 'switch-card';

const switchCardVariants = cva(
  'group relative flex cursor-pointer items-center justify-between border border-input shadow-xs select-none hover:bg-accent hover:text-accent-foreground',
  {
    variants: {
      sizesx: {
        default: 'min-h-9 rounded-lg p-4 py-[0.8rem]',
        sm: 'min-h-8 rounded-md px-4 py-2',
      },
    },
    defaultVariants: {
      sizesx: 'default',
    },
  },
);

interface SwitchCardProps
  extends React.ComponentPropsWithoutRef<typeof Switch>, VariantProps<typeof switchCardVariants> {
  description?: string | JSX.Element;
  disabledOnlyStyles?: boolean;
  isLoading?: boolean;
  label: string | JSX.Element;
}

/**
 * @param disabledOnlyStyles **optional** Apply disabled styles without functionality. Helps when you are using react-hook-form
 * and the disabled functionality is on the fieldset Ref: https://react-hook-form.com/docs/useform/handlesubmit
 */
export function SwitchCard({
  className,
  description,
  disabled = false,
  disabledOnlyStyles = false,
  id,
  isLoading = false,
  label,
  sizesx,
  ...props
}: SwitchCardProps) {
  return (
    <Label className="block" htmlFor={id ?? SWITCH_ID}>
      <div
        className={cn(
          switchCardVariants({ sizesx }),
          {
            'cursor-not-allowed border-input/50 text-muted-foreground hover:bg-transparent [&:hover_*]:text-muted-foreground!':
              disabled || disabledOnlyStyles || isLoading,
          },
          className,
        )}
        role="button"
      >
        <div className="pr-4">
          <div className={cn('font-medium', sizesx === 'sm' ? 'text-sm' : 'text-base')}>
            {label}
          </div>
          {description ? (
            <div className="text-sm text-muted-foreground group-hover:text-accent-foreground">
              {description}
            </div>
          ) : null}
        </div>
        <Switch
          name={id ?? SWITCH_ID}
          id={id ?? SWITCH_ID}
          disabled={disabled || isLoading}
          {...props}
        />
        {isLoading && <LoadingAddon type="plain" />}
      </div>
    </Label>
  );
}
